/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// src/components/SplashScreen.tsx
import React, { useState, useEffect } from 'react';

const LOADBAR_MS = 3200;  // durata animazione barra
const COUNTDOWN_START = 10;

interface SplashScreenProps {
  onDismiss: () => void;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ onDismiss }) => {
  const [countdown, setCountdown] = useState<number | null>(null);

  // Avvia il countdown al termine della barra di caricamento
  useEffect(() => {
    const t = setTimeout(() => setCountdown(COUNTDOWN_START), LOADBAR_MS);
    return () => clearTimeout(t);
  }, []);

  // Decrementa ogni secondo; a 0 lancia onDismiss
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) { onDismiss(); return; }
    const t = setTimeout(() => setCountdown((c) => (c ?? 1) - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onDismiss]);

  return (
    <div className="splash-container" onClick={onDismiss} style={splashStyle}>
      <style>{`
        @keyframes logo3d {
          0%,100% { transform: perspective(700px) rotateY(-8deg) rotateX(3deg) translateY(0px);
                    filter: drop-shadow(12px 18px 28px rgba(0,0,0,0.95)) drop-shadow(-3px -3px 10px rgba(255,107,53,0.22)); }
          33%      { transform: perspective(700px) rotateY(7deg) rotateX(-2deg) translateY(-12px);
                    filter: drop-shadow(18px 24px 32px rgba(0,0,0,0.95)) drop-shadow(-6px -6px 14px rgba(255,107,53,0.38)); }
          66%      { transform: perspective(700px) rotateY(-4deg) rotateX(5deg) translateY(-7px);
                    filter: drop-shadow(8px 20px 24px rgba(0,0,0,0.95)) drop-shadow(-2px -4px 10px rgba(255,107,53,0.18)); }
        }
        .splash-logo-3d {
          animation: logo3d 5.5s ease-in-out infinite;
          will-change: transform, filter;
          transform-style: preserve-3d;
        }
        .loading-bar-container { width: 280px; height: 4px; background: #222; border-radius: 2px; margin: 20px auto 0; overflow: hidden; }
        .loading-bar-progress  { height: 100%; background: linear-gradient(90deg,#ff6b35,#ff8c42); border-radius: 2px;
                                  animation: loadbar 3.2s ease-in-out forwards; box-shadow: 0 0 8px rgba(255,107,53,0.6); }
        @keyframes loadbar { 0%{width:0%} 20%{width:28%} 45%{width:55%} 72%{width:80%} 90%{width:95%} 100%{width:100%} }
        @keyframes pulse-countdown {
          0%,100% { opacity: 1; transform: scale(1); }
          50%     { opacity: 0.6; transform: scale(0.92); }
        }
        .splash-countdown-num {
          animation: pulse-countdown 1s ease-in-out infinite;
          display: inline-block;
        }
      `}</style>
      <div className="splash-content">
        <img
          src="/images/LogoIrtilac_TM.png"
          alt="Logo IRTILAC"
          className="splash-logo-3d"
          style={{ width: '300px', height: 'auto', marginBottom: '20px' }}
        />

        <h1 style={{ color: '#ff6b35', margin: '10px 0' }}>DOS518</h1>
        <p style={{ color: '#aaa' }}>Sistema Gestione Operazioni Antincendio Boschivo</p>

        <div className="loading-bar-container">
          <div className="loading-bar-progress"></div>
        </div>

        <div style={{ marginTop: '30px', minHeight: '40px' }}>
          {countdown === null ? (
            <p style={{ fontSize: '0.8rem', opacity: 0.4, margin: 0 }}>
              Caricamento…
            </p>
          ) : (
            <p style={{ fontSize: '0.85rem', color: '#ff8c42', margin: 0 }}>
              Tocca per iniziare — avvio in{' '}
              <span className="splash-countdown-num" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {countdown}
              </span>{' '}s
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const splashStyle: React.CSSProperties = {
  height: '100vh',
  width: '100vw',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#111',
  cursor: 'pointer',
  textAlign: 'center'
};